/**
 * UZ-8537: Loads a product list block that was skipped on the full page load.
 *
 * blocks/product-list.liquid renders this element instead of the list for lists further down the
 * page. When it approaches the viewport, the section is fetched via the Section Rendering API
 * (where the block always renders in full) and the placeholder is replaced with the block content.
 *
 * - The request keeps the current URL (path, market, query), so the list renders in the same
 *   context as the page. Several deferred lists in one section share one request.
 * - Product cards, carousels and energy labels are custom elements and upgrade on insertion; the
 *   wishlist app picks up new buttons on its own. Scripts inside the fetched markup (e.g. from app
 *   blocks) are re-created so they run like on a full page load.
 */

/** @type {Map<string, Promise<Document>>} */
const sectionRequests = new Map();

/**
 * @param {string} sectionId
 * @returns {Promise<Document>}
 */
const fetchSection = (sectionId) => {
  const cached = sectionRequests.get(sectionId);
  if (cached) return cached;

  const request = (async () => {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('sections', sectionId);

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

    const data = await response.json();
    const html = data?.[sectionId];
    if (typeof html !== 'string') throw new Error(`Section ${sectionId} missing in response`);

    return new DOMParser().parseFromString(html, 'text/html');
  })();

  // A failed request may be retried by the next caller.
  request.catch(() => sectionRequests.delete(sectionId));
  sectionRequests.set(sectionId, request);
  return request;
};

/**
 * Scripts inserted via DOM APIs do not run, so recreate them. They are processed in document
 * order and external scripts are awaited, so an inline script never runs before its dependency
 * (dynamically inserted classic scripts are async by default).
 * @param {Node[]} nodes
 * @returns {Promise<void>}
 */
const runScripts = async (nodes) => {
  const scripts = nodes.flatMap((node) => {
    if (!(node instanceof Element)) return [];
    return node.matches('script') ? [node] : Array.from(node.querySelectorAll('script'));
  });

  for (const oldScript of scripts) {
    const type = oldScript.getAttribute('type') || '';
    if (type && type !== 'module' && !type.includes('javascript')) continue;

    const script = document.createElement('script');
    for (const { name, value } of oldScript.attributes) script.setAttribute(name, value);
    script.textContent = oldScript.textContent;

    /** @type {Promise<void> | undefined} */
    const settled = script.src
      ? new Promise((resolve) => {
          script.addEventListener('load', () => resolve(), { once: true });
          script.addEventListener('error', () => resolve(), { once: true });
        })
      : undefined;

    if (script.src && type !== 'module') script.async = false;
    oldScript.replaceWith(script);
    if (settled) await settled;
  }
};

class DeferredProductList extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #loading = false;

  connectedCallback() {
    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load();
      },
      // Start well before the list is visible so it is in place even on fast scrolling.
      { rootMargin: '1200px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** @param {number} [attempt] */
  async #load(attempt = 1) {
    if (this.#loading) return;
    this.#loading = true;

    const { sectionId, blockId } = this.dataset;

    try {
      if (!sectionId || !blockId) throw new Error('Missing section or block id');

      const doc = await fetchSection(sectionId);
      const source =
        doc.getElementById(`shopify-block-${blockId}`) ??
        doc.querySelector(`[data-product-list-block-id="${CSS.escape(blockId)}"]`)?.parentElement;
      if (!source) throw new Error(`Block ${blockId} missing in section ${sectionId}`);

      const nodes = Array.from(source.childNodes, (node) => document.importNode(node, true));
      const container = this.parentElement;
      this.replaceWith(...nodes);
      await runScripts(nodes);
      container?.dispatchEvent(new CustomEvent('deferred-product-list:loaded', { bubbles: true }));
    } catch (error) {
      this.#loading = false;
      if (attempt < 2) {
        window.setTimeout(() => this.#load(attempt + 1), 1000);
        return;
      }
      console.warn('[deferred-product-list]', error);
      // Do not leave an empty reserved gap behind if the list cannot be loaded.
      this.style.minHeight = '0';
    }
  }
}

if (!customElements.get('deferred-product-list')) {
  customElements.define('deferred-product-list', DeferredProductList);
}
